/**
 * Date display helpers for the shopfloor app.
 *
 * Backend timestamps arrive as PHP `Y-m-d H:i:s` strings, which Safari cannot
 * parse (`new Date('2026-10-16 09:12:00')` → Invalid Date). Normalise first,
 * then render with the shared `dd.mm.yy hh:mm` formatter.
 */
import { formatDisplayDate, formatDisplayDateTime } from '@yuksalish/ui'

const PHP_DATE_TIME = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

/**
 * `2026-10-16 09:12:00` → `2026-10-16T09:12:00` (local wall-clock, no shift).
 * A date-only value gets an explicit local midnight: `new Date('2026-10-16')`
 * is parsed as UTC, which would render a shifted hour outside UTC.
 */
export function shopfloorApiTimestamp(value: string | null | undefined): string {
  const text = (value ?? '').trim()
  if (text === '') return ''
  const match = PHP_DATE_TIME.exec(text)
  if (match) {
    const seconds = match[6] ?? '00'
    return `${match[1]}-${match[2]}-${match[3]}T${match[4]}:${match[5]}:${seconds}`
  }
  if (DATE_ONLY.test(text)) return `${text}T00:00:00`
  return text
}

/** `16.10.26 09:12` — empty string when the value is missing/unparseable. */
export function shopfloorDateTime(value: string | null | undefined): string {
  return formatDisplayDateTime(shopfloorApiTimestamp(value))
}

/** `16.10.26` — for date-only columns. */
export function shopfloorDate(value: string | null | undefined): string {
  return formatDisplayDate(shopfloorApiTimestamp(value))
}

/**
 * Prefers the recorded timestamp (has a time component) and falls back to the
 * date-only column, so a row never renders an ISO string or an empty cell.
 */
export function shopfloorRowTimestamp(
  timestamp: string | null | undefined,
  dateOnly: string | null | undefined,
): string {
  return shopfloorDateTime(timestamp) || shopfloorDate(dateOnly)
}
